import React, { useContext } from 'react';
import InfoMessage from '.././InfoMessage';
import { filterSearchResults } from '../../data/utils/filterSearchResults';
import { filterSportsFromSearchResults } from '../../data/utils/filterSportsFromSearchResults';
import { iconCategoryCssClass } from '../../data/utils/iconCategoryCssClass';
import { getLabel } from '../../data/labels';
import LiveBadge from '../LiveBadge';
import { urls } from '../..';
import { XRouterNavigateContext } from '../seo/xrouter/XRouterNavigateContext';
import { usePrematchData } from '../../prematch/PrematchDataProvider';
import { useSearchData } from './SearchDataProvider';
import { useSearchLiveData } from './SearchLiveDataProvider';

// Component that shows both live and prematch results filtered by search
export default function CombinedResults(props) {
  const { search, toggle } = props;
  const xrouter = useContext(XRouterNavigateContext);
  const liveData = useSearchLiveData();
  const searchData = useSearchData();
  const prematchData = usePrematchData();

  // Filter live matches
  const liveMatches = liveData?.matches
    ? filterSearchResults(
        liveData.matches,
        search,
        'MATCH',
        match => match.desc
      ).map(match => ({
        ...match,
        timeLabel: match.time,
        isLive: true,
      }))
    : [];

  // Filter prematch events
  const sports = filterSportsFromSearchResults(searchData, search);
  const prematchEvents = sports.flatMap(sport =>
    sport.tournaments.flatMap(tournament =>
      tournament.events.map(event => ({
        ...event,
        timeLabel: `${event.dateShort} | ${event.time}`,
        isLive: false,
      }))
    )
  );

  const combined = [...liveMatches, ...prematchEvents];

  if (combined.length === 0) {
    return <InfoMessage message={getLabel('general.no_events')} />;
  }

  function openEvent(e) {
    if (e.isLive) {
      xrouter.navigate(
        `${urls.base}/${urls.live.base}/${urls.live.event}/${e.id}`,
        { data: e, state: e }
      );
    } else {
      const uniqueId = `${e.sportRef}_${e.categoryRef}_${e.tournamentRef}`;
      xrouter.navigate(
        `${urls.base}/${urls.prematch.base}/${urls.prematch.tournament}/${uniqueId}/${urls.prematch.event}/${e.id}`,
        { data: e, state: e }
      );
    }
    toggle();
  }

  return (
    <>
      {combined.map(event => {
        const iconClass =
          (event.isLive
            ? liveData.sportById[event.sportRef]
            : prematchData.sportById[event.sportRef]
          )?.categoryById[event.categoryRef]?.iconClass || null;
        const categoryIcon = iconCategoryCssClass(
          event.sportRef,
          event.categoryRef,
          iconClass
        );
        const desc = `${event.sportDesc} - ${event.tournamentDesc}`;

        return (
          <div
            key={event.id + (event.isLive ? '_live' : '_pre')}
            className="p-1 bg-cs1 flex flex-col justify-center border-b border-b-cs7"
            onClick={() => openEvent(event)}
          >
            <div className="grid grid-cols-12">
              <div className="col-start-1 col-end-2 h-full flex items-center">
                <div className="p-1 flex flex-col justify-center border border-cs-9 rounded-md">
                  <span className={`my-auto inline-block w-[24px] h-[24px] categoryIcon ${categoryIcon}`} />
                </div>
              </div>
              <div className="col-start-2 col-end-13 pl-2 flex flex-col">
                <div className="flex items-center">
                  <span className="font-medium text-xs text-cs9 uppercase truncate">
                    {event.timeLabel} | {desc}
                  </span>
                  {event.isLive && <div className="ml-1 flex items-center"><LiveBadge /></div>}
                </div>
                <span className="font-medium text-xs text-cs-10 truncate">{event.team1}</span>
                <span className="font-medium text-xs text-cs-10 truncate">{event.team2}</span>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
